// App.tsx
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { FirebaseAuthProvider, useAuth } from "./contexts/FirebaseAuthContext";
import { LibraryProvider } from "./contexts/LibraryContext";
import { LiveKitRoom } from "@livekit/components-react";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import Dashboard from "./pages/books/Dashboard";
import GenreSelection from "./pages/onboarding/GenreSelection";
import ReadingGoals from "./pages/onboarding/ReadingGoals";
import OnboardingComplete from "./pages/onboarding/OnboardingComplete";
import OnboardingRoute from "./components/OnboardingRoute";
import PrivateRoute from "./components/PrivateRoute";
import Library from "./pages/books/Library";
import Upload from "./pages/books/Upload";
import Reader from "./pages/books/Reader";
import Reading from "./pages/books/Reading";
import Discover from "./pages/books/Discover";
import BookClub from "./pages/books/BookClub";
import Layout from "./components/layout/Layout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Type assertion to fix TS2786
const LiveKitRoomTyped = LiveKitRoom as React.FC<
  React.PropsWithChildren<{
    serverUrl: string;
    token: string | (() => Promise<string>);
    connect?: boolean;
    onConnected?: () => void;
    onError?: (error: Error) => void;
    autoSubscribe?: boolean;
  }>
>;

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component for LiveKitRoom
const LiveKitWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [liveKitToken, setLiveKitToken] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const fetchToken = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_LIVEKIT_URL}/api/livekit-token`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const data = await response.json();
      const { token } = data;
      if (!token) {
        throw new Error("No token received");
      }
      console.log(
        "[LiveKitWrapper] Fetched token:",
        token.slice(0, 10) + "..."
      );
      setLiveKitToken(token);
      setConnectionError(null);
    } catch (error) {
      console.error("[LiveKitWrapper] Failed to fetch token:", error);
      setConnectionError(
        "Failed to fetch LiveKit token. Make sure the TTS server is running."
      );
      setLiveKitToken(null);
    }
  };

  useEffect(() => {
    fetchToken();
  }, []);

  if (connectionError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-500 text-center">
          {connectionError}. Retrying...
        </div>
      </div>
    );
  }

  if (!liveKitToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <LiveKitRoomTyped
      serverUrl={
        process.env.REACT_APP_LIVEKIT_URL ||
        "wss://smata-test-jbzk3nqm.livekit.cloud"
      }
      token={liveKitToken}
      connect={true}
      autoSubscribe={true}
      onConnected={() => {
        console.log("[LiveKitWrapper] Connected to LiveKit room");
        setConnectionError(null);
      }}
      onError={(error) => {
        console.error("[LiveKitWrapper] LiveKit error:", error);
        setConnectionError("LiveKit connection failed");
      }}
    >
      {children}
    </LiveKitRoomTyped>
  );
};

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  console.log("AppRoutes render:", { user, loading });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={user ? "/dashboard" : "/login"} />}
      />
      <Route
        path="/login"
        element={!user ? <Login /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/signup"
        element={!user ? <Signup /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/forgot-password"
        element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/onboarding/genres"
        element={
          <OnboardingRoute>
            <GenreSelection />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/goals"
        element={
          <OnboardingRoute>
            <ReadingGoals />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding/complete"
        element={
          <OnboardingRoute>
            <OnboardingComplete />
          </OnboardingRoute>
        }
      />
      <Route
        element={
          <Layout>
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          </Layout>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/books/upload" element={<Upload />} />
        <Route path="/books/library" element={<Library />} />
        <Route
          path="/books/reading"
          element={
            <LiveKitWrapper>
              <Reading />
            </LiveKitWrapper>
          }
        />
        <Route
          path="/books/reading/:bookId"
          element={
            <LiveKitWrapper>
              <Reading />
            </LiveKitWrapper>
          }
        />
        <Route path="/books/discover" element={<Discover />} />
        <Route path="/books/book-club" element={<BookClub />} />
        <Route path="/books/reader/:bookId" element={<Reader />} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  console.log("App component rendering");
  return (
    <ErrorBoundary>
      <Router>
        <FirebaseAuthProvider>
          <LibraryProvider>
            <AppRoutes />
          </LibraryProvider>
        </FirebaseAuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
